"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import * as Flags from "country-flag-icons/react/3x2";

/**
 * Currency presentation: SVG country flags for fiat (per brand rules — no
 * emoji), flat monogram token badges for stablecoins.
 */
export const CURRENCY_META: Record<
  string,
  { name: string; country?: keyof typeof Flags; color?: string; mono?: string }
> = {
  USD: { name: "US Dollar", country: "US" },
  EUR: { name: "Euro", country: "EU" },
  GBP: { name: "British Pound", country: "GB" },
  MXN: { name: "Mexican Peso", country: "MX" },
  USDC: { name: "USD Coin", color: "#2775CA", mono: "$" },
  USDT: { name: "Tether USD", color: "#26A17B", mono: "T" },
  DAI: { name: "Dai", color: "#F5AC37", mono: "D" },
  EURC: { name: "Euro Coin", color: "#264C9F", mono: "€" },
  PYUSD: { name: "PayPal USD", color: "#0070E0", mono: "P" },
};

export function CurrencyBadge({ code, size = 20 }: { code: string; size?: number }) {
  const meta = CURRENCY_META[code];
  const Flag = meta?.country ? Flags[meta.country] : null;

  if (Flag) {
    return (
      <span
        className="relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={{ width: size, height: size }}
        aria-hidden
      >
        <Flag style={{ height: size, width: "auto", maxWidth: "none" }} />
        {/* Hairline ring keeps light flag edges (US white stripes) crisp on any background. */}
        <span className="absolute inset-0 rounded-full" style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.14)" }} />
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        background: meta?.color ?? "var(--c-charcoal)",
        color: "var(--c-white)",
        fontSize: size * 0.55,
        lineHeight: 1,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
      }}
      aria-hidden
    >
      {meta?.mono ?? code[0]}
    </span>
  );
}

export function CurrencySelect({
  codes,
  value,
  onChange,
}: {
  codes: readonly string[];
  value: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="field flex items-center gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <CurrencyBadge code={value} />
        <span className="font-normal">{value}</span>
        <span className="min-w-0 flex-1 truncate text-[13px]" style={{ color: "var(--c-ink-soft)" }}>
          {CURRENCY_META[value]?.name}
        </span>
        <ChevronsUpDown size={14} style={{ color: "var(--c-ink-soft)" }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-[6px] border py-1"
          style={{ background: "var(--c-white)", borderColor: "var(--c-hairline)" }}
        >
          {codes.map((c) => (
            <button
              key={c}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] transition-colors duration-150 hover:bg-[var(--c-cream)]"
              style={{ fontWeight: c === value ? 500 : 300 }}
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
            >
              <CurrencyBadge code={c} />
              <span className="w-[52px] font-normal">{c}</span>
              <span className="min-w-0 flex-1 truncate" style={{ color: "var(--c-ink-soft)" }}>
                {CURRENCY_META[c]?.name}
              </span>
              {c === value && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
